import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Award,
  BookOpen,
  GraduationCap,
  Globe,
  ShieldCheck,
  Sparkles,
  CheckCircle2,
  Building2,
  MapPin,
} from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { Sunflower, SunflowerMini } from "@/components/site/Sunflower";
import { IBCCES_STAMP_URL, ADHD_CERT_URL } from "@/lib/assets";

const credentials = [
  {
    icon: Award,
    title: "Certified Autism Specialist (CAS)",
    body: "Nada Zahabi holds the CAS credential — IBCCES's flagship certification for educators and clinicians supporting autistic learners. Requires bachelor's plus 10 years of relevant experience, or master's plus 2 years, and 14 hours of evidence-based continuing education.",
  },
  {
    icon: BookOpen,
    title: "14 Hours of Evidence-Based Training",
    body: "Coursework spans autism overview, behaviour, communication, comorbidities, early identification, IEPs, and parent/caregiver strategies — all renewed every two years to stay current.",
  },
  {
    icon: ShieldCheck,
    title: "Recognised in 130+ Countries",
    body: "IBCCES credentials are recognised across schools, healthcare systems, and autism-friendly destinations worldwide — including across the MENA region and within the UAE.",
  },
  {
    icon: Globe,
    title: "Aligned with Global Standards",
    body: "Programs are reviewed by IBCCES's expert board, and several certifications are ASHA / AOTA approved — meaning the practice you receive aligns with international clinical standards.",
  },
];

const programs = [
  "Certified Autism Specialist (CAS)",
  "Autism Certificate (AC)",
  "Advanced Certified Autism Specialist (ACAS)",
  "Advanced Autism Certificate (AAC)",
  "Certified Autism Destination™",
  "Autism Certified City™",
];

export default function Certifications() {
  return (
    <div data-testid="certifications-page">
      {/* HERO */}
      <section className="section-pad relative overflow-hidden petal-bg">
        <div className="absolute -top-14 -right-14 opacity-35 pointer-events-none hidden md:block">
          <Sunflower size={240} className="animate-slowspin" />
        </div>
        <div className="relative grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-brand-leafDeep bg-brand-honey/50 border border-brand-sun/40 rounded-full px-4 py-1.5 mb-6 w-fit">
              <SunflowerMini size={18} />
              Certifications & Credentials
            </span>
            <h1 className="font-heading font-normal text-[2.75rem] sm:text-5xl lg:text-[4.25rem] leading-[1.02] tracking-tight">
              Trained to a global
              <br />
              <span className="sunray-underline">autism standard.</span>
            </h1>
            <p className="mt-7 text-brand-muted text-base md:text-lg leading-relaxed max-w-xl font-body">
              Nada Zahabi is recognised by IBCCES — the International Board of
              Credentialing and Continuing Education Standards — the body
              behind the world's most widely-adopted autism credentials. Below
              is exactly what that means for you and your child.
            </p>
          </div>

          <div className="lg:col-span-5">
            <Reveal>
              <div className="bg-white rounded-[32px] p-8 border-2 border-brand-sun/40 shadow-earthyLg flex flex-col items-center text-center">
                <img
                  data-testid="cert-ibcces-stamp"
                  src={IBCCES_STAMP_URL}
                  alt="IBCCES Autism Certificate — issued to Nada Zahabi, ID AC25444439, valid through 08/17/2027"
                  className="w-full max-w-[300px] aspect-square object-contain"
                />
                <p className="font-brand text-2xl text-brand-leafDeep mt-4">
                  IBCCES
                </p>
                <p className="text-xs uppercase tracking-[0.22em] text-brand-muted mt-2 font-body">
                  Autism Certificate · AC25444439
                </p>
                <p className="text-xs text-brand-muted mt-1 font-body">
                  Valid through 08/17/2027
                </p>
                <a
                  data-testid="cert-ibcces-website-btn"
                  href="https://ibcces.org/about/"
                  target="_blank"
                  rel="noreferrer"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-sun hover:bg-brand-sunDeep text-brand-ink px-5 py-2.5 text-sm font-medium transition-colors"
                >
                  Visit IBCCES.org <ArrowRight size={14} />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHAT IS IBCCES */}
      <section className="section-pad bg-brand-ivory">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-brand-leafDeep bg-brand-honey/50 border border-brand-sun/40 rounded-full px-4 py-1.5 mb-6 w-fit">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-sun" />
              About IBCCES
            </span>
            <h2 className="font-heading font-normal text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
              The global standard for autism and neurodiversity training.
            </h2>
            <p className="mt-6 text-brand-muted leading-relaxed font-body">
              IBCCES was founded to give educators, therapists, healthcare
              providers, and support staff a credential they could trust — and
              that families could rely on. With autism rates now at 1 in 100
              children globally, IBCCES equips professionals with research-
              based, position-specific training so the support you receive
              meets a measurable bar.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {credentials.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div className="bg-white rounded-[24px] p-7 border-2 border-brand-line hover:border-brand-sun/50 h-full transition-colors">
                  <div className="h-11 w-11 rounded-2xl bg-brand-sun/25 text-brand-sunDeep grid place-items-center mb-5">
                    <c.icon size={20} />
                  </div>
                  <h3 className="font-heading text-lg mb-2">{c.title}</h3>
                  <p className="text-sm text-brand-muted leading-relaxed font-body">
                    {c.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* IBCCES PROGRAMS */}
      <section className="section-pad relative overflow-hidden">
        <div className="absolute bottom-0 right-0 opacity-15 pointer-events-none hidden md:block">
          <Sunflower size={280} showLeaves className="animate-slowspin" />
        </div>
        <div className="relative grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <h2 className="font-heading font-normal text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
              The IBCCES family of credentials.
            </h2>
            <p className="mt-6 text-brand-muted leading-relaxed font-body">
              IBCCES doesn't just certify individuals. It also certifies whole
              cities, schools, and destinations — building entire ecosystems
              that work for autistic individuals and their families.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
            {programs.map((p, i) => (
              <Reveal key={p} delay={i * 0.06}>
                <div className="bg-brand-ivory rounded-2xl border-2 border-brand-line px-5 py-4 flex items-center gap-3">
                  <CheckCircle2 size={18} className="text-brand-leafDeep shrink-0" />
                  <span className="text-sm text-brand-ink font-body">{p}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ADHD COACH CERTIFICATION */}
      <section className="section-pad bg-brand-ivory relative overflow-hidden">
        <div className="absolute -bottom-16 -left-16 opacity-20 pointer-events-none hidden md:block">
          <Sunflower size={260} showLeaves className="animate-slowspin" />
        </div>

        <div className="relative grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <div
                data-testid="cert-adhd-card"
                className="bg-white rounded-[32px] p-6 border-2 border-brand-sun/40 shadow-earthyLg"
              >
                <img
                  data-testid="cert-adhd-image"
                  src={ADHD_CERT_URL}
                  alt="ADHD Coach Certification — issued to Nada Zahabi by Efficient Coach, certificate number 26-13137, dated 04 May 2026"
                  className="w-full rounded-2xl object-contain bg-brand-cream"
                />
                <div className="mt-5 text-center">
                  <p className="font-brand text-2xl text-brand-leafDeep">
                    ADHD Coach Certification
                  </p>
                  <p className="text-xs uppercase tracking-[0.22em] text-brand-muted mt-2 font-body">
                    Efficient Coach · Cert. 26-13137
                  </p>
                  <p className="text-xs text-brand-muted mt-1 font-body">
                    Issued 04 May 2026
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-brand-leafDeep bg-brand-honey/50 border border-brand-sun/40 rounded-full px-4 py-1.5 mb-6 w-fit">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-sun" />
              ADHD Specialisation
            </span>
            <h2 className="font-heading font-normal text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
              A certified ADHD coach —
              <br />
              <span className="sunray-underline">multi-accredited.</span>
            </h2>
            <p className="mt-6 text-brand-muted leading-relaxed font-body">
              Beyond autism, Nada is a <strong>Certified ADHD Coach</strong> — formally
              trained to support neurodivergent children, teens, and the parents who
              love them. The program covers ADHD neuroscience, executive function,
              emotional regulation, parent-coaching frameworks, and ethical
              practice standards. The certification is recognised by{" "}
              <strong>five independent accreditation bodies</strong>, giving you the
              assurance that the support you receive meets multiple international
              benchmarks at once.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {[
                {
                  short: "CPD",
                  title: "Centre of CPD Excellence",
                  body: "UK-based Continuing Professional Development standard — verifies the program meets recognised CPD learning outcomes.",
                },
                {
                  short: "NCCAP",
                  title: "National Certification Council for Activity Professionals",
                  body: "US accreditation body that certifies professionals working in therapeutic, educational, and developmental activity programs.",
                },
                {
                  short: "CMA",
                  title: "Complementary Medical Association",
                  body: "International association recognising practitioners trained in evidence-informed complementary and integrative approaches.",
                },
                {
                  short: "IPHM",
                  title: "International Practitioners of Holistic Medicine",
                  body: "Global accreditation body that audits training programs to ensure they meet professional, ethical, and safety standards.",
                },
                {
                  short: "icahp",
                  title: "International Compliance Assurance for Holistic Practitioners",
                  body: "Compliance and ethics standard ensuring practitioners follow recognised codes of conduct and client-care guidelines.",
                },
                {
                  short: "EC",
                  title: "Efficient Coach (Issuing Body)",
                  body: "The training and credentialing institution that issued the ADHD Coach Certification to Nada Zahabi (Cert. 26-13137).",
                },
              ].map((a, i) => (
                <Reveal key={a.short} delay={i * 0.06}>
                  <div className="bg-white rounded-2xl border-2 border-brand-line hover:border-brand-sun/50 transition-colors p-5 h-full">
                    <div className="flex items-start gap-3">
                      <div className="h-11 min-w-[2.75rem] rounded-2xl bg-brand-sun/25 text-brand-sunDeep grid place-items-center font-heading text-sm px-2">
                        {a.short}
                      </div>
                      <div>
                        <h4 className="font-heading text-base leading-snug">
                          {a.title}
                        </h4>
                        <p className="text-xs text-brand-muted mt-1.5 font-body leading-relaxed">
                          {a.body}
                        </p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* NADA'S CREDENTIALS */}
      <section className="section-pad bg-brand-leafDeep text-brand-cream grain relative overflow-hidden">
        <div className="absolute -top-16 -left-16 opacity-25 pointer-events-none">
          <Sunflower size={280} showLeaves className="animate-slowspin" />
        </div>
        <div className="relative grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-brand-honey bg-white/10 border border-brand-honey/40 rounded-full px-4 py-1.5 mb-6 w-fit">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-sun" />
              Nada's credentials
            </span>
            <h2 className="font-heading font-normal text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
              Twenty-one years of education.
              <br />
              <span className="text-brand-honey">A decade in neurodiversity.</span>
            </h2>

            <div className="mt-6 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-2 text-xs bg-white/10 border border-white/20 rounded-full px-3 py-1.5 text-brand-cream font-body">
                <span className="text-base leading-none">🇨🇦</span> Canadian
              </span>
              <span className="inline-flex items-center gap-2 text-xs bg-white/10 border border-white/20 rounded-full px-3 py-1.5 text-brand-cream font-body">
                <MapPin size={12} /> Dubai based
              </span>
              <span className="inline-flex items-center gap-2 text-xs bg-brand-sun/25 border border-brand-sun/40 rounded-full px-3 py-1.5 text-brand-honey font-body font-medium">
                IBCCES AC25444439
              </span>
            </div>

            <p className="mt-6 text-brand-cream/85 leading-relaxed font-body">
              Nada combines deep early-childhood leadership with international
              autism credentialing — a rare blend that lets her bridge what
              works in the classroom with what's needed at home.
            </p>

            {/* Inline IBCCES stamp */}
            <div className="mt-8 inline-block">
              <img
                src={IBCCES_STAMP_URL}
                alt="IBCCES Autism Certificate"
                className="h-32 w-32 object-contain rounded-2xl bg-white p-2 shadow-sunny"
              />
            </div>
          </div>
          <div className="lg:col-span-7 grid gap-4">
            {[
              {
                icon: GraduationCap,
                title: "21 Years as Kindergarten Principal & Curriculum Coordinator",
                body: "Two decades leading early-childhood teams, designing curriculum, and mentoring educators across Dubai.",
              },
              {
                icon: Sparkles,
                title: "10 Years of Education in Neurodivergency",
                body: "A decade focused specifically on supporting children with ADHD, autism, and other neurodevelopmental differences.",
              },
              {
                icon: Award,
                title: "IBCCES CAS + Certified ADHD Coach",
                body: "Two internationally recognised credentials: IBCCES Certified Autism Specialist (AC25444439) and Efficient Coach ADHD Coach Certification (26-13137) — both renewed and maintained.",
              },
              {
                icon: Building2,
                title: "Education Director — Early Childhood",
                body: "Active leadership role shaping inclusive curriculum and staff training programs.",
              },
            ].map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div className="bg-white/8 border border-white/15 rounded-2xl p-6 flex items-start gap-4 hover:bg-white/12 transition-colors">
                  <div className="h-11 w-11 rounded-2xl bg-brand-sun/20 text-brand-sun grid place-items-center shrink-0">
                    <c.icon size={20} />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-brand-cream">
                      {c.title}
                    </h3>
                    <p className="text-sm text-brand-cream/80 mt-1 font-body leading-relaxed">
                      {c.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad">
        <div className="bg-gradient-to-br from-brand-sun/30 via-brand-honey/40 to-brand-leafSoft/25 rounded-[32px] p-10 md:p-14 border-2 border-brand-sun/40 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-2xl md:text-3xl">
              Ready to work with a certified specialist?
            </h3>
            <p className="text-sm text-brand-muted mt-2 font-body">
              Book Nada's 30-minute discovery session and map your next step.
            </p>
          </div>
          <Link
            data-testid="cert-book-btn"
            to="/contact"
            className="rounded-full bg-brand-ink hover:bg-brand-leafDeep text-brand-cream px-7 py-3.5 text-sm font-medium inline-flex items-center gap-2 transition-colors shadow-earthy"
          >
            Contact <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
